import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../../assets/theme';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import {
  saveLocalDynamicLessonState,
  useDynamicLessonStateContext,
} from '../../../../hooks/useCreateLessonState';
import useDebounce from '../../../../hooks/useDebounce';
import { useMetrics } from '../../../../hooks/useMetrics';
import {
  getSubcategories,
  SubjectOption,
  subjectOptionMapper,
  subjectOptionMapperV2,
} from '../../../../utils/createLessonPageUtils';
import { Input } from '../../../_atoms/_Input';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { FormContainer } from '../AnyContentOrSkillTab';
import { FloatingSelect } from './FloatingSelect';

const NovelStudiesForm = ({
  //!Depreciated when we remove the hasNewBlockAccess flag
  lessonIntent,
  setErrorMessage,
  errorMessage,
  hasNewBlockAccess,
}: {
  lessonIntent: {
    id: string;
    displayName: string;
    comingSoon?: boolean;
    hasMoreDetails?: boolean;
    dropdownOptions?: { value: string; displayName: string }[];
  };
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  hasNewBlockAccess: boolean;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const targetSkill = useDynamicLessonStateContext(c => c.dynamicLessonState.targetSkill);
  const addedOwnTargetSkill = useDynamicLessonStateContext(
    c => c.dynamicLessonState.addedOwnTargetSkill,
  );
  const novel = useDynamicLessonStateContext(c => c.dynamicLessonState.novel);
  const chapter = useDynamicLessonStateContext(c => c.dynamicLessonState.chapter);

  const [currentNovel, setCurrentNovel] = useState<string | undefined>(novel);
  const debouncedNovel = useDebounce(currentNovel, 1000);
  const [currentChapter, setCurrentChapter] = useState<string | undefined>(chapter);
  const debouncedChapter = useDebounce(currentChapter, 1000);
  const [currentAddedOwnTargetSkill, setCurrentAddedOwnTargetSkill] = useState<
    string | undefined
  >(addedOwnTargetSkill);
  const debouncedAddedOwnTargetSkill = useDebounce(currentAddedOwnTargetSkill, 1000);

  useEffect(() => {
    if (debouncedNovel !== undefined) {
      dispatchAndSync({
        type: 'SET_NOVEL',
        payload: currentNovel,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNovel]);

  useEffect(() => {
    if (debouncedChapter !== undefined) {
      dispatchAndSync({
        type: 'SET_CHAPTER',
        payload: currentChapter,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedChapter]);
  useEffect(() => {
    if (debouncedAddedOwnTargetSkill !== undefined) {
      dispatchAndSync({
        type: 'SET_ADDED_OWN_TARGET_SKILL',
        payload: currentAddedOwnTargetSkill,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAddedOwnTargetSkill]);

  const handleCreatePrompt = ({ prompt }: { prompt: string }) =>
    dispatchAndSync({
      type: 'ON_PROMPT_CHANGE',
      payload: prompt,
    });

  const metrics = useMetrics();

  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;
  const findDropdownOptions = (lessonIntentId: string) => {
    return (
      subjectOptions['ela']?.lessonIntents?.find(l => l.id === lessonIntentId)
        ?.dropdownOptions || []
    );
  };

  useEffect(() => {
    //if there is no focus initiated from the URL we can set it to the first in the array
    if (!targetSkill) {
      const firstOption = findDropdownOptions(lessonIntent.id)[0];
      firstOption && handleFocusChange(firstOption.value);
    }
  }, []);

  const handleFocusChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    dispatchAndSync({
      type: 'SET_TARGET_SKILL',
      payload: value as string,
    });
  };

  const formErrorText = useMemo(() => {
    if (!currentNovel) {
      return `Don't forget to enter a novel`;
    }
    if (
      (targetSkill === 'other' || targetSkill?.includes('other_')) &&
      !currentAddedOwnTargetSkill
    ) {
      return `Don't forget to tell us your focus skill`;
    }
    return undefined;
  }, [currentNovel, targetSkill, currentAddedOwnTargetSkill]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const focusName =
      targetSkill === 'other' || targetSkill?.includes('other_')
        ? currentAddedOwnTargetSkill
        : findDropdownOptions(lessonIntent.id).find(o => o.value === targetSkill)
            ?.displayName;

    saveLocalDynamicLessonState({ novel: currentNovel });

    handleCreatePrompt({
      prompt: `A ${lessonIntent.displayName} lesson on ${currentNovel} ${
        currentChapter ? `chapter ${currentChapter} ` : ''
      }focusing on ${focusName}`,
    });
    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonIntent: lessonIntent.id,
      currentNovel,
      currentChapter,
      targetSkill,
      addedOwnTargetSkill: currentAddedOwnTargetSkill,
      prompt: `A ${lessonIntent.displayName} lesson on ${currentNovel} ${
        currentChapter ? `chapter ${currentChapter} ` : ''
      }focusing on ${focusName}`,
    });
  };

  return (
    <>
      <InputsWrapper style={{ width: '100%' }}>
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="Title and Author"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="The Giver by Lois Lowry"
          value={currentNovel}
          onChange={e => {
            if (errorMessage) setErrorMessage(undefined);
            setCurrentNovel(e.target.value);
          }}
        />
        <Input
          inputStyle={{
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
            height: 42,
          }}
          label="Chapter (optional)"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="12"
          value={currentChapter}
          onChange={e => setCurrentChapter(e.target.value)}
        />
      </InputsWrapper>
      <FloatingSelect
        onSelect={handleFocusChange}
        options={findDropdownOptions(lessonIntent.id)}
        value={targetSkill}
        label="Focus"
      />
      {(targetSkill === 'other' || targetSkill?.includes('other_')) && (
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="What would you like to focus on?"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="Character Development"
          value={currentAddedOwnTargetSkill}
          onChange={e => {
            setErrorMessage(undefined);
            setCurrentAddedOwnTargetSkill(e.target.value);
          }}
        />
      )}
      <GetLessonButton onClick={handlePressNext} $disabled={!!formErrorText}>
        Next
      </GetLessonButton>
    </>
  );
};
const ReadAloudForm = ({
  lessonIntent,
  setErrorMessage,
  errorMessage,
  hasNewBlockAccess,
}: {
  lessonIntent: {
    id: string;
    displayName: string;
    comingSoon?: boolean;
    hasMoreDetails?: boolean;
    dropdownOptions?: { value: string; displayName: string }[];
  };
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  hasNewBlockAccess: boolean;
}) => {
  const metrics = useMetrics();
  const targetSkill = useDynamicLessonStateContext(c => c.dynamicLessonState.targetSkill);
  const addedOwnTargetSkill = useDynamicLessonStateContext(
    c => c.dynamicLessonState.addedOwnTargetSkill,
  );
  const novel = useDynamicLessonStateContext(c => c.dynamicLessonState.novel);
  const chapter = useDynamicLessonStateContext(c => c.dynamicLessonState.chapter);

  const [currentNovel, setCurrentNovel] = useState<string | undefined>(novel);
  const debouncedNovel = useDebounce(currentNovel, 1000);
  const [currentChapter, setCurrentChapter] = useState<string | undefined>(chapter);
  const debouncedChapter = useDebounce(currentChapter, 1000);
  const [currentAddedOwnTargetSkill, setCurrentAddedOwnTargetSkill] = useState<
    string | undefined
  >(addedOwnTargetSkill);
  const debouncedAddedOwnTargetSkill = useDebounce(currentAddedOwnTargetSkill, 1000);

  useEffect(() => {
    if (debouncedNovel !== undefined) {
      dispatchAndSync({
        type: 'SET_NOVEL',
        payload: currentNovel,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNovel]);

  useEffect(() => {
    if (debouncedChapter !== undefined) {
      dispatchAndSync({
        type: 'SET_CHAPTER',
        payload: currentChapter,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedChapter]);
  useEffect(() => {
    if (debouncedAddedOwnTargetSkill !== undefined) {
      dispatchAndSync({
        type: 'SET_ADDED_OWN_TARGET_SKILL',
        payload: currentAddedOwnTargetSkill,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAddedOwnTargetSkill]);
  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;
  const findDropdownOptions = (lessonIntentId: string) => {
    return (
      subjectOptions['ela']?.lessonIntents?.find(l => l.id === lessonIntentId)
        ?.dropdownOptions || []
    );
  };

  useEffect(() => {
    //if there is no focus initiated from the URL we can set it to the first in the array
    if (!targetSkill) {
      const firstOption = findDropdownOptions(lessonIntent.id)[0];
      firstOption && handleFocusChange(firstOption.value);
    }
  }, []);

  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  const handleCreatePrompt = ({ prompt }: { prompt: string }) =>
    dispatchAndSync({
      type: 'ON_PROMPT_CHANGE',
      payload: prompt,
    });

  const handleFocusChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    dispatchAndSync({
      type: 'SET_TARGET_SKILL',
      payload: value as string,
    });
  };

  const formErrorText = useMemo(() => {
    if (!currentNovel) {
      return `Don't forget to enter a novel`;
    }
    if (
      (targetSkill === 'other' || targetSkill?.includes('other_')) &&
      !currentAddedOwnTargetSkill
    ) {
      return `Don't forget to tell us your focus skill`;
    }
    return undefined;
  }, [currentNovel, targetSkill, currentAddedOwnTargetSkill]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const focusName =
      targetSkill === 'other' || targetSkill?.includes('other_')
        ? currentAddedOwnTargetSkill
        : findDropdownOptions(lessonIntent.id).find(o => o.value === targetSkill)
            ?.displayName;

    saveLocalDynamicLessonState({ novel: currentNovel });
    handleCreatePrompt({
      prompt: `Students will analyze ${focusName} in ${currentNovel}${
        currentChapter ? ` chapter ${currentChapter}.` : '.'
      }`,
    });
    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonIntent: lessonIntent.id,
      novel: currentNovel,
      chapter: currentChapter,
      targetSkill,
      addedOwnTargetSkill: currentAddedOwnTargetSkill,
      prompt: `A ${lessonIntent.displayName} lesson on ${currentNovel} ${
        currentChapter ? `chapter ${currentChapter} ` : ''
      }focusing on ${focusName}`,
    });
  };

  return (
    <>
      <InputsWrapper style={{ width: '100%' }}>
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="Title and Author"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="The Giver by Lois Lowry"
          value={currentNovel}
          onChange={e => {
            if (errorMessage) setErrorMessage(undefined);
            setCurrentNovel(e.target.value);
          }}
        />
        <Input
          inputStyle={{
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
            height: 42,
          }}
          label="Chapter (optional)"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="12"
          value={currentChapter}
          onChange={e => setCurrentChapter(e.target.value)}
        />
      </InputsWrapper>
      <FloatingSelect
        onSelect={handleFocusChange}
        options={findDropdownOptions(lessonIntent.id)}
        value={targetSkill}
        label="Focus"
      />
      {(targetSkill === 'other' || targetSkill?.includes('other_')) && (
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="What would you like to focus on?"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="Character Development"
          value={currentAddedOwnTargetSkill}
          onChange={e => {
            setErrorMessage(undefined);
            setCurrentAddedOwnTargetSkill(e.target.value);
          }}
        />
      )}
      <GetLessonButton onClick={handlePressNext} $disabled={!!formErrorText}>
        Next
      </GetLessonButton>
    </>
  );
};

const GenericSkillReleaseForm = ({
  lessonIntent,
  setErrorMessage,
}: {
  lessonIntent: SubjectOption;
  setErrorMessage: (message: string | undefined) => void;
  hasNewBlockAccess: boolean;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  const handleCreatePrompt = ({ prompt }: { prompt: string }) =>
    dispatchAndSync({
      type: 'ON_PROMPT_CHANGE',
      payload: prompt,
    });

  const addedOwnTargetSkill = useDynamicLessonStateContext(
    c => c.dynamicLessonState.addedOwnTargetSkill,
  );
  const lessonTheme = useDynamicLessonStateContext(c => c.dynamicLessonState.lessonTheme);

  const [currentLessonTheme, setCurrentLessonTheme] = useState<string | undefined>(
    lessonTheme,
  );
  const debouncedLessonTheme = useDebounce(currentLessonTheme, 1000);
  const [currentAddedOwnTargetSkill, setCurrentAddedOwnTargetSkill] = useState<
    string | undefined
  >(addedOwnTargetSkill);
  const debouncedAddedOwnTargetSkill = useDebounce(currentAddedOwnTargetSkill, 1000);

  useEffect(() => {
    if (debouncedLessonTheme !== undefined) {
      dispatchAndSync({
        type: 'SET_LESSON_THEME',
        payload: currentLessonTheme,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLessonTheme]);

  useEffect(() => {
    if (debouncedAddedOwnTargetSkill !== undefined) {
      dispatchAndSync({
        type: 'SET_ADDED_OWN_TARGET_SKILL',
        payload: currentAddedOwnTargetSkill,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAddedOwnTargetSkill]);

  const metrics = useMetrics();

  const formErrorText = useMemo(() => {
    if (!currentAddedOwnTargetSkill) {
      return `Don't forget to tell us your target skill`;
    }
    return undefined;
  }, [currentAddedOwnTargetSkill]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const lessonPrompt =
      currentAddedOwnTargetSkill && lessonIntent.prompt
        ? `${lessonIntent.prompt.replace(/{{focusName}}/g, currentAddedOwnTargetSkill)} ${
            currentLessonTheme
              ? `Include lesson materials about ${currentLessonTheme}.`
              : ''
          }`
        : '';

    handleCreatePrompt({
      prompt: lessonPrompt,
    });

    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonIntent: lessonIntent.id,
      focus,
      prompt: lessonPrompt,
    });
  };

  return (
    <>
      <Input
        inputStyle={{
          height: 42,
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        label="Target Skill"
        labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
        placeholder="Finding area and volume, Identifying changes in matter, Comparing cultures"
        value={currentAddedOwnTargetSkill}
        onChange={e => {
          setCurrentAddedOwnTargetSkill(e.target.value);
        }}
      />
      <Input
        tooltip={`Create a lesson students will really connect to! Make it about student interests or cross-curricular topics.`}
        inputStyle={{
          height: 42,
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        label="Make it about… (optional)"
        labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
        placeholder="Book or movie, history or science topic, or anything students are interested in..."
        value={currentLessonTheme}
        onChange={e => {
          setCurrentLessonTheme(e.target.value);
        }}
      />

      <GetLessonButton onClick={handlePressNext} $disabled={false}>
        Next
      </GetLessonButton>
    </>
  );
};
const ELASkillReleaseForm = ({
  lessonIntent,
  subject,
  setErrorMessage,
  errorMessage,
  hasNewBlockAccess,
}: {
  lessonIntent: SubjectOption;
  subject?: string;
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
  hasNewBlockAccess: boolean;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  const handleCreatePrompt = ({ prompt }: { prompt: string }) =>
    dispatchAndSync({
      type: 'ON_PROMPT_CHANGE',
      payload: prompt,
    });

  const isLargerThan650 = useBreakPoint(650);
  const subjectOptions = hasNewBlockAccess ? subjectOptionMapperV2 : subjectOptionMapper;
  const findDropdownOptions = useCallback(
    (lessonIntentId: string) => {
      if (!subject) return [];
      const dropdownOptions = subjectOptions[subject]?.lessonIntents?.find(
        l => l.id === lessonIntentId,
      )?.dropdownOptions;

      if (dropdownOptions && dropdownOptions.length > 0) {
        return dropdownOptions;
      }
      return [];
    },
    [subject, subjectOptions],
  );

  const subjectFocus = useDynamicLessonStateContext(
    c => c.dynamicLessonState.subjectFocus,
  );
  const lessonTheme = useDynamicLessonStateContext(c => c.dynamicLessonState.lessonTheme);

  const targetSkill = useDynamicLessonStateContext(c => c.dynamicLessonState.targetSkill);

  const addedOwnTargetSkill = useDynamicLessonStateContext(
    c => c.dynamicLessonState.addedOwnTargetSkill,
  );
  const [currentLessonTheme, setCurrentLessonTheme] = useState<string | undefined>(
    lessonTheme,
  );
  const debouncedLessonTheme = useDebounce(currentLessonTheme, 1000);
  const [currentAddedOwnTargetSkill, setCurrentAddedOwnTargetSkill] = useState<
    string | undefined
  >(addedOwnTargetSkill);
  const debouncedAddedOwnTargetSkill = useDebounce(currentAddedOwnTargetSkill, 1000);

  useEffect(() => {
    if (debouncedLessonTheme !== undefined) {
      dispatchAndSync({
        type: 'SET_LESSON_THEME',
        payload: currentLessonTheme,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLessonTheme]);

  useEffect(() => {
    if (debouncedAddedOwnTargetSkill !== undefined) {
      dispatchAndSync({
        type: 'SET_ADDED_OWN_TARGET_SKILL',
        payload: currentAddedOwnTargetSkill,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAddedOwnTargetSkill]);

  const focusAreaDropdownOptions = useMemo(() => {
    if (!subject) return [];
    //check if the dropdown options have subcategories
    const hasSubcategories = getSubcategories(subject, lessonIntent.id);
    return hasSubcategories;
  }, [lessonIntent.id, subject]);

  const metrics = useMetrics();
  const handleFocusChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    dispatchAndSync({
      type: 'SET_TARGET_SKILL',
      payload: value as string,
    });
  };

  const handleFocusAreaChange = (value: string | number | boolean) => {
    setErrorMessage(undefined);
    dispatchAndSync({
      type: 'SET_SUBJECT_FOCUS',
      payload: value as string,
    });

    const firstOption = findDropdownOptions(lessonIntent.id).find(
      option => option.subCategory === value,
    );
    firstOption &&
      dispatchAndSync({ type: 'SET_TARGET_SKILL', payload: firstOption.value });
  };

  useEffect(() => {
    if (focusAreaDropdownOptions) {
      handleFocusAreaChange(focusAreaDropdownOptions[0]?.value);
    }
  }, [focusAreaDropdownOptions]);

  useEffect(() => {
    //if there is no focus initiated from the URL we can set it to the first in the array
    if (!focusAreaDropdownOptions && !targetSkill) {
      const firstOption = findDropdownOptions(lessonIntent.id)[0];
      firstOption && handleFocusChange(firstOption.value);
    }
  }, []);

  const filteredDroppdownOptions = useMemo(() => {
    if (!focusAreaDropdownOptions) return findDropdownOptions(lessonIntent.id);
    return findDropdownOptions(lessonIntent.id).filter(option => {
      return option.subCategory === subjectFocus;
    });
  }, [focusAreaDropdownOptions, findDropdownOptions, lessonIntent.id, subjectFocus]);

  const focusName =
    targetSkill === 'other' || targetSkill?.includes('other_')
      ? currentAddedOwnTargetSkill
      : findDropdownOptions(lessonIntent.id).find(o => o.value === targetSkill)
          ?.displayName;

  const formErrorText = useMemo(() => {
    if (
      (targetSkill === 'other' || targetSkill?.includes('other_')) &&
      !currentAddedOwnTargetSkill
    ) {
      return `Don't forget to tell us your target skill`;
    }
    return undefined;
  }, [targetSkill, currentAddedOwnTargetSkill]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const endPromptBasedOnFocusArea =
      subjectFocus === 'readingSkills'
        ? 'reading comprehension and analysis.'
        : 'writing.';

    const lessonPrompt =
      focusName && lessonIntent.prompt
        ? `${lessonIntent.prompt.replace(
            /{{focusName}}/g,
            focusName,
          )} ${endPromptBasedOnFocusArea} ${
            currentLessonTheme
              ? `Include lesson materials about ${currentLessonTheme}.`
              : ''
          }`
        : '';

    handleCreatePrompt({
      prompt: lessonPrompt,
    });

    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonIntent: lessonIntent.id,
      targetSkill,
      addedOwnTargetSkill: currentAddedOwnTargetSkill,
      subjectFocus,
      prompt: lessonPrompt,
    });
  };

  return (
    <>
      <InputsWrapper
        style={{
          gridTemplateColumns:
            focusAreaDropdownOptions && isLargerThan650 ? '2fr 4fr' : '1fr',
        }}
      >
        {focusAreaDropdownOptions && (
          <FloatingSelect
            onSelect={handleFocusAreaChange}
            options={focusAreaDropdownOptions}
            value={subjectFocus}
            label="Subject Focus"
            placeholder=""
          />
        )}
        <FloatingSelect
          onSelect={handleFocusChange}
          options={filteredDroppdownOptions}
          value={targetSkill}
          label="Target Skill"
          placeholder=""
        />
      </InputsWrapper>
      {(targetSkill === 'other' || targetSkill?.includes('other_')) && (
        <Input
          inputStyle={{
            height: 42,
            boxShadow: theme.effects.floatingCreatorElementShadow,
            border: 'none',
          }}
          label="What would you like to focus on?"
          labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
          placeholder="Thesis statements"
          value={currentAddedOwnTargetSkill}
          onChange={e => {
            if (errorMessage) setErrorMessage(undefined);
            setCurrentAddedOwnTargetSkill(e.target.value);
          }}
        />
      )}
      <Input
        tooltip={`Create a lesson students will really connect to! Make it about student interests or cross-curricular topics.`}
        inputStyle={{
          height: 42,
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        label="Make it about… (optional)"
        labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
        placeholder="Book or movie, history or science topic, or anything students are interested in..."
        value={currentLessonTheme}
        onChange={e => {
          setCurrentLessonTheme(e.target.value);
        }}
      />

      <GetLessonButton onClick={handlePressNext} $disabled={false}>
        Next
      </GetLessonButton>
    </>
  );
};
const TypeMoreDetailsForm = ({
  lessonIntent,
  setErrorMessage,
  errorMessage,
}: {
  lessonIntent: SubjectOption;
  errorMessage: string | undefined;
  setErrorMessage: (message: string | undefined) => void;
}) => {
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);

  const handleCreatePrompt = ({ prompt }: { prompt: string }) =>
    dispatchAndSync({
      type: 'ON_PROMPT_CHANGE',
      payload: prompt,
    });

  const isLargerThan600 = useBreakPoint(600);

  const needSupportWith = useDynamicLessonStateContext(
    c => c.dynamicLessonState.needSupportWith,
  );

  const [currentNeedMoreSupportWith, setCurrentNeedMoreSupportWith] = useState<
    string | undefined
  >(needSupportWith);
  const debouncedNeedsMoreSupportWith = useDebounce(currentNeedMoreSupportWith, 1000);

  useEffect(() => {
    if (debouncedNeedsMoreSupportWith !== undefined) {
      dispatchAndSync({
        type: 'SET_NEEDS_SUPPORT',
        payload: currentNeedMoreSupportWith,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNeedsMoreSupportWith]);

  const metrics = useMetrics();

  const formErrorText = useMemo(() => {
    if (!currentNeedMoreSupportWith) {
      return `Don't forget to tell us what your students need support with`;
    }
    return undefined;
  }, [currentNeedMoreSupportWith]);

  const handlePressNext = () => {
    if (formErrorText) {
      setErrorMessage(formErrorText);
      return;
    }

    const lessonPrompt =
      lessonIntent.prompt && currentNeedMoreSupportWith
        ? `${lessonIntent.prompt.replace(/{{focusName}}/g, currentNeedMoreSupportWith)}`
        : '';

    handleCreatePrompt({
      prompt: lessonPrompt,
    });

    metrics.logEvent('CreatePage.NextButtonClicked', {
      lessonIntent: lessonIntent.id,
      focus,
      prompt: lessonPrompt,
    });
  };

  return (
    <>
      <TextArea
        rows={isLargerThan600 ? 3 : 6}
        tooltip={
          lessonIntent.id === 'reviewOrReteach'
            ? `Tell the misconceptions or specific concepts students struggle with.`
            : `Tell the topic and main takeaways you need to cover. Be specific!`
        }
        textAreaStyled={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        label={
          lessonIntent.id === 'reviewOrReteach'
            ? 'Students need more support with...'
            : 'Students will learn…'
        }
        labelStyle={{ fontSize: 16, fontWeight: 600, color: theme.colors.black }}
        placeholder={
          lessonIntent.id === 'reviewOrReteach'
            ? 'What concept, skill, or misconception do you want to focus on?'
            : 'What concept, skill, or content do you need to teach?'
        }
        value={currentNeedMoreSupportWith}
        onChange={e => {
          if (errorMessage) setErrorMessage(undefined);
          setCurrentNeedMoreSupportWith(e.target.value);
        }}
      />

      <GetLessonButton onClick={handlePressNext} $disabled={false}>
        Next
      </GetLessonButton>
    </>
  );
};

const InputsWrapper = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 20px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const ExtraDetailsForm = ({
  lessonIntent,
  hasNewBlockAccess,
  subject,
}: {
  lessonIntent: SubjectOption;
  hasNewBlockAccess: boolean;
  subject?: string;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  return (
    <>
      <FormContainer style={{ maxWidth: 900, width: '100%' }}>
        {lessonIntent.id === 'novelStudy' && (
          <NovelStudiesForm
            lessonIntent={lessonIntent}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
            hasNewBlockAccess={hasNewBlockAccess}
          />
        )}
        {lessonIntent.id === 'readAloud' && (
          <ReadAloudForm
            lessonIntent={lessonIntent}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
            hasNewBlockAccess={hasNewBlockAccess}
          />
        )}
        {(lessonIntent.id === 'teachSomethingNew' ||
          lessonIntent.id === 'reviewOrReteach') && (
          <TypeMoreDetailsForm
            lessonIntent={lessonIntent}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
          />
        )}
        {lessonIntent.id === 'practiceASkill' && subject === 'ela' && (
          <ELASkillReleaseForm
            lessonIntent={lessonIntent}
            subject={subject}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
            hasNewBlockAccess={hasNewBlockAccess}
          />
        )}
        {lessonIntent.id === 'practiceASkill' && subject !== 'ela' && (
          <GenericSkillReleaseForm
            lessonIntent={lessonIntent}
            setErrorMessage={setErrorMessage}
            hasNewBlockAccess={hasNewBlockAccess}
          />
        )}
        {(lessonIntent.id === 'partsOfAnEssay' ||
          lessonIntent.id === 'writingSkills' ||
          lessonIntent.id === 'shortResponse' ||
          lessonIntent.id === 'readingStrategies') && (
          //TODO this is v1 full lesson and will be removed when the flag is removed
          <ELASkillReleaseForm
            lessonIntent={lessonIntent}
            subject={subject}
            setErrorMessage={setErrorMessage}
            errorMessage={errorMessage}
            hasNewBlockAccess={hasNewBlockAccess}
          />
        )}
      </FormContainer>
      {errorMessage && (
        <p style={{ color: theme.colors.red, textAlign: 'center' }}>{errorMessage}</p>
      )}
    </>
  );
};

const GetLessonButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: 20px auto;

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

export default ExtraDetailsForm;
